import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { getAdminToken } from "../components/Auth";
import { toast } from "sonner";
import {
  Users, Server, Key, Wrench, Plus, Trash2, Edit2,
  RefreshCw, Save, Eye, EyeOff, Activity, MessageSquare, DollarSign,
  AlertTriangle, Copy, Database, Download, Upload, Send
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import CustomerCombobox from "../components/CustomerCombobox";
import F2poolReadonlyLinks from "../components/F2poolReadonlyLinks";
import OkxTradeAlerts from "../components/OkxTradeAlerts";
import MarketPosts from "../components/MarketPosts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const formatDowntime = (minutes) => {
  const m = Math.max(0, minutes || 0);
  if (m < 60) return `${m}m`;
  const hours = Math.floor(m / 60);
  const mins = m % 60;
  if (hours < 24) return mins ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
};

const AdminPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [signupRequests, setSignupRequests] = useState([]);
  // request_id -> chosen customer_id ("" = create new customer)
  const [signupLinkChoice, setSignupLinkChoice] = useState({});
  // request_id -> {worker_name, machine_type_id, quantity, monthly_fee}
  const [signupAssign, setSignupAssign] = useState({});
  const [viabtcWorkers, setViabtcWorkers] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [farmStats, setFarmStats] = useState({
    machines_online: 2430,
    machines_offline: 10,
    total_hashrate: "850 TH/s",
    total_hashrate_by_coin: "",
    fluctuation: 5,
    zec_machines: 0,
    zec_hashrate: ""
  });
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [f2poolSettings, setF2poolSettings] = useState({ account: "", currency: "litecoin", enabled: false, token: "", has_token: false });
  const [savingF2pool, setSavingF2pool] = useState(false);
  const [testingF2pool, setTestingF2pool] = useState(false);
  const [f2poolTestResult, setF2poolTestResult] = useState(null);
  const [f2poolBalances, setF2poolBalances] = useState(null);
  const [loadingF2poolBalances, setLoadingF2poolBalances] = useState(false);
  const [viaBtcSettings, setViaBtcSettings] = useState({
    access_key: "",
    secret_key: "",
    enabled: false
  });
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [testingApi, setTestingApi] = useState(false);
  const [apiTestResult, setApiTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showHelperModal, setShowHelperModal] = useState(false);
  const [helperAccounts, setHelperAccounts] = useState([]);
  const [helperForm, setHelperForm] = useState({ username: "", password: "", display_name: "" });
  const [savingHelper, setSavingHelper] = useState(false);

  // Forms
  const [accountForm, setAccountForm] = useState({ customer_id: "", username: "", password: "", worker_name: "", viabtc_api_key: "", watcher_url: "" });
  const [logForm, setLogForm] = useState({ customer_id: "", machine_info: "", description: "" });
  const [mainWatcherUrl, setMainWatcherUrl] = useState("");
  const [savingWatcher, setSavingWatcher] = useState(false);
  const [botSettings, setBotSettings] = useState({
    auto_reminders_enabled: false,
    offline_alerts_enabled: false,
    admin_phone: "+9613022005",
    fleet_monitor_phone: "",
    reminder_day: 1,
    reminder_interval_days: 3
  });
  const [messageHistory, setMessageHistory] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [repairs, setRepairs] = useState([]);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairForm, setRepairForm] = useState({ customer_id: "", description: "", cost: "", cost_tbd: false });
  const [paymentClaims, setPaymentClaims] = useState([]);
  const [qrInfo, setQrInfo] = useState(null);
  const [selectedCustomerForSend, setSelectedCustomerForSend] = useState("");
  const [selectedPaymentForPdf, setSelectedPaymentForPdf] = useState("");
  const [unpaidPaymentsForSelected, setUnpaidPaymentsForSelected] = useState([]);
  const [sendingStats, setSendingStats] = useState(false);
  const [sendingPdf, setSendingPdf] = useState(false);
  const [viabtcHealth, setViabtcHealth] = useState(null);
  const [reliabilityDays, setReliabilityDays] = useState(7);
  const [reliabilityMachines, setReliabilityMachines] = useState([]);
  const [farmTrend, setFarmTrend] = useState([]);
  const [loadingReliability, setLoadingReliability] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [monthlyMachines, setMonthlyMachines] = useState([]);
  const [loadingMonthly, setLoadingMonthly] = useState(false);

  const fetchViabtcHealth = async () => {
    try {
      const r = await axios.get(`${API}/admin/viabtc-health`);
      setViabtcHealth(r.data);
    } catch {
      setViabtcHealth(null);
    }
  };

  const fetchReliability = async (days = reliabilityDays) => {
    setLoadingReliability(true);
    try {
      const [relRes, trendRes] = await Promise.all([
        axios.get(`${API}/machine-history/reliability?days=${days}`),
        axios.get(`${API}/machine-history/farm-trend?days=${Math.max(days, 14)}`),
      ]);
      setReliabilityMachines(relRes.data.machines || []);
      setFarmTrend(trendRes.data.trend || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReliability(false);
    }
  };

  const fetchHelperAccounts = async () => {
    try {
      const res = await axios.get(`${API}/helper-accounts`);
      setHelperAccounts(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddHelper = async () => {
    if (!helperForm.username.trim() || !helperForm.password.trim()) {
      toast.error("Username and password are required");
      return;
    }
    setSavingHelper(true);
    try {
      await axios.post(`${API}/helper-accounts`, helperForm);
      toast.success("Helper account created");
      setShowHelperModal(false);
      setHelperForm({ username: "", password: "", display_name: "" });
      fetchHelperAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create helper account");
    } finally {
      setSavingHelper(false);
    }
  };

  const handleToggleHelperActive = async (helper) => {
    try {
      await axios.put(`${API}/helper-accounts/${helper.id}`, { active: !helper.active });
      fetchHelperAccounts();
    } catch (e) {
      toast.error("Failed to update helper account");
    }
  };

  const handleResetHelperPassword = async (helper) => {
    const newPassword = window.prompt(`New password for "${helper.username}":`);
    if (!newPassword || !newPassword.trim()) return;
    try {
      await axios.put(`${API}/helper-accounts/${helper.id}`, { password: newPassword.trim() });
      toast.success("Password updated");
    } catch (e) {
      toast.error("Failed to reset password");
    }
  };

  const handleDeleteHelper = async (helper) => {
    if (!window.confirm(`Remove helper account "${helper.username}"? They will no longer be able to log in.`)) return;
    try {
      await axios.delete(`${API}/helper-accounts/${helper.id}`);
      toast.success("Helper account removed");
      fetchHelperAccounts();
    } catch (e) {
      toast.error("Failed to remove helper account");
    }
  };

  const fetchMonthlyDowntime = async (month = selectedMonth) => {
    setLoadingMonthly(true);
    try {
      const res = await axios.get(`${API}/machine-history/monthly?month=${month}`);
      setMonthlyMachines(res.data.machines || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMonthly(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPaymentClaims();
    fetchViabtcHealth();
    fetchReliability();
    fetchMonthlyDowntime();
    fetchHelperAccounts();
    const t = setInterval(fetchPaymentClaims, 30000);
    const t2 = setInterval(fetchViabtcHealth, 5 * 60 * 1000); // re-probe every 5 minutes
    return () => { clearInterval(t); clearInterval(t2); };
  }, []);

  // When a customer is selected, fetch their unpaid invoices
  useEffect(() => {
    if (!selectedCustomerForSend) {
      setUnpaidPaymentsForSelected([]);
      setSelectedPaymentForPdf("");
      return;
    }
    axios.get(`${API}/payments`)
      .then(r => setUnpaidPaymentsForSelected(
        (r.data || []).filter(p => p.customer_id === selectedCustomerForSend && p.status !== "paid")
      ))
      .catch(() => setUnpaidPaymentsForSelected([]));
  }, [selectedCustomerForSend]);

  const fetchPaymentClaims = async () => {
    try {
      const res = await axios.get(`${API}/payment-claims?status=pending`);
      setPaymentClaims(res.data || []);
    } catch (e) {}
  };

  const approveClaim = async (id) => {
    if (!window.confirm("Approve this payment claim? The customer's invoice will be marked PAID and they'll receive a confirmation WhatsApp.")) return;
    try {
      await axios.post(`${API}/payment-claims/${id}/approve`);
      toast.success("Approved");
      fetchPaymentClaims();
    } catch (e) {
      toast.error("Failed to approve");
    }
  };

  const rejectClaim = async (id) => {
    const reason = window.prompt("Rejection reason (optional):", "Could not verify the payment.");
    if (reason === null) return;
    try {
      await axios.post(`${API}/payment-claims/${id}/reject`, { reason });
      toast.success("Rejected");
      fetchPaymentClaims();
    } catch (e) {
      toast.error("Failed to reject");
    }
  };

  const dismissClaim = async (id) => {
    try {
      await axios.post(`${API}/payment-claims/${id}/dismiss`);
      toast.success("Dismissed — nothing sent to customer");
      fetchPaymentClaims();
    } catch (e) {
      toast.error("Failed to dismiss");
    }
  };

  const showQrCode = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/qr-code`);
      setQrInfo(res.data);
    } catch (e) {
      toast.error("Failed to load QR code");
    }
  };

  const fetchData = async () => {
    try {
      const [accountsRes, customersRes, statsRes, logsRes, viaBtcRes, f2poolRes, botRes, repairsRes, signupsRes] = await Promise.all([
        axios.get(`${API}/customer-accounts`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/farm-stats`),
        axios.get(`${API}/maintenance-logs`),
        axios.get(`${API}/viabtc-settings`),
        axios.get(`${API}/f2pool/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/bot-settings`),
        axios.get(`${API}/repairs`),
        axios.get(`${API}/signup-requests`).catch(() => ({ data: [] }))
      ]);
      setAccounts(accountsRes.data);
      setSignupRequests(signupsRes.data || []);
      // Only when there are pending signups: load the ViaBTC worker list and
      // machine types for the one-shot "assign machine on approve" dropdowns.
      // Fired without await so the slow ViaBTC call never delays the page.
      if ((signupsRes.data || []).length > 0) {
        axios.get(`${API}/viabtc/workers`).then(r => setViabtcWorkers(r.data?.workers || [])).catch(() => {});
        axios.get(`${API}/machine-types`).then(r => setMachineTypes(r.data || [])).catch(() => {});
      }
      setCustomers((customersRes.data || []).slice().sort((a,b) => (a.name||'').localeCompare(b.name||'', undefined, {sensitivity:'base'})));
      setFarmStats(statsRes.data);
      setMaintenanceLogs(logsRes.data);
      setRepairs(repairsRes.data);
      setViaBtcSettings({
        access_key: viaBtcRes.data.access_key || "",
        secret_key: viaBtcRes.data.secret_key || "",
        enabled: viaBtcRes.data.enabled || false,
        watcher_key: viaBtcRes.data.watcher_key || ""
      });
      setF2poolSettings({
        account: f2poolRes.data?.account || "",
        currency: f2poolRes.data?.currency || "litecoin",
        enabled: f2poolRes.data?.enabled || false,
        token: "",
        has_token: !!f2poolRes.data?.has_token,
      });
      if (viaBtcRes.data.watcher_key) {
        setMainWatcherUrl(`https://www.viabtc.com/en/observer/worker?access_key=${viaBtcRes.data.watcher_key}&coin=LTC`);
      }
      setBotSettings(botRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ---- Signup requests (customer self-signup, admin approves) ----
  const setAssign = (reqId, patch) =>
    setSignupAssign((prev) => ({ ...prev, [reqId]: { ...(prev[reqId] || {}), ...patch } }));

  const handleApproveSignup = async (req) => {
    const customerId = signupLinkChoice[req.id] || "";
    const assign = signupAssign[req.id] || {};
    const body = {};
    if (customerId) body.customer_id = customerId;
    if (assign.worker_name) body.worker_name = assign.worker_name;
    if (assign.machine_type_id) {
      body.machine = {
        machine_type_id: assign.machine_type_id,
        quantity: parseInt(assign.quantity) || 1,
        monthly_fee: assign.monthly_fee === "" || assign.monthly_fee === undefined ? undefined : parseFloat(assign.monthly_fee),
      };
    }
    try {
      const res = await axios.post(`${API}/signup-requests/${req.id}/approve`, body);
      const bits = [];
      if (res.data.linked_existing_by_phone) {
        bits.push(`same phone found — linked to existing customer "${res.data.customer_name}" (no duplicate created)`);
      } else {
        bits.push(res.data.created_new_customer ? `new customer "${req.name}" added` : "linked to existing customer");
      }
      if (res.data.worker_linked) bits.push("ViaBTC worker set");
      if (res.data.machine_added) bits.push("machine + billing set");
      if (res.data.whatsapp_sent) bits.push("customer notified on WhatsApp");
      toast.success(`Account created: ${bits.join(", ")}`);
      if (!res.data.whatsapp_sent) {
        toast.error(
          `WhatsApp message NOT delivered (${res.data.whatsapp_error || "unknown reason"}). ` +
          `Check the phone number, then use the send button next to the account to retry.`,
          { duration: 12000 }
        );
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };

  const handleSendWelcome = async (account) => {
    try {
      const res = await axios.post(`${API}/customer-accounts/${account.id}/send-welcome`);
      toast.success(`Login details sent to ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const handleRejectSignup = async (req) => {
    try {
      await axios.post(`${API}/signup-requests/${req.id}/reject`);
      toast.success("Request rejected");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  // Auto-create accounts for all customers
  const handleAutoCreateAccounts = async () => {
    try {
      const res = await axios.post(`${API}/auto-create-accounts`);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error("Failed to create accounts");
    }
  };

  // Save farm stats
  const handleSaveFarmStats = async () => {
    try {
      await axios.put(`${API}/farm-stats`, farmStats);
      toast.success("Farm stats updated");
    } catch (e) {
      toast.error("Failed to update stats");
    }
  };

  // Save ViaBTC settings
  const handleSaveViaBtcSettings = async () => {
    try {
      await axios.put(`${API}/viabtc-settings`, null, {
        params: {
          access_key: viaBtcSettings.access_key,
          secret_key: viaBtcSettings.secret_key,
          enabled: viaBtcSettings.enabled
        }
      });
      toast.success("ViaBTC API settings saved");
    } catch (e) {
      toast.error("Failed to save ViaBTC settings");
    }
  };

  const handleSaveF2poolSettings = async () => {
    setSavingF2pool(true);
    try {
      const body = {
        account: f2poolSettings.account,
        currency: f2poolSettings.currency,
        enabled: f2poolSettings.enabled,
      };
      if (f2poolSettings.token) body.token = f2poolSettings.token;   // only send when changing
      const r = await axios.put(`${API}/f2pool/settings`, body);
      toast.success("F2Pool settings saved");
      setF2poolSettings({ ...f2poolSettings, token: "", has_token: !!r.data?.has_token });
    } catch (e) {
      toast.error("Failed to save F2Pool settings");
    }
    setSavingF2pool(false);
  };

  const handleTestF2pool = async () => {
    setTestingF2pool(true); setF2poolTestResult(null);
    try {
      const body = { account: f2poolSettings.account, currency: f2poolSettings.currency };
      if (f2poolSettings.token) body.token = f2poolSettings.token;
      const r = await axios.post(`${API}/f2pool/test`, body);
      setF2poolTestResult(r.data);
    } catch (e) {
      setF2poolTestResult({ success: false, error: e?.response?.data?.detail || "Request failed" });
    }
    setTestingF2pool(false);
  };

  // Fetch unpaid (unsettled) balances across all F2Pool accounts, grouped by coin.
  const handleCheckF2poolBalances = async (refresh = false) => {
    setLoadingF2poolBalances(true);
    try {
      const r = await axios.get(`${API}/admin/f2pool-balances`, { params: refresh ? { refresh: true } : {} });
      setF2poolBalances(r.data);
    } catch (e) {
      setF2poolBalances({ enabled: false, error: e?.response?.data?.detail || "Request failed" });
    }
    setLoadingF2poolBalances(false);
  };

  // Test ViaBTC API connection
  const handleTestViaBtcApi = async () => {
    setTestingApi(true);
    setApiTestResult(null);
    try {
      // First save the settings
      await axios.put(`${API}/viabtc-settings`, null, {
        params: {
          access_key: viaBtcSettings.access_key,
          secret_key: viaBtcSettings.secret_key,
          enabled: viaBtcSettings.enabled
        }
      });
      
      // Then test the connection
      const res = await axios.post(`${API}/viabtc-test`);
      setApiTestResult(res.data);
      if (res.data.success) {
        toast.success("API connection successful!");
      } else {
        toast.error(res.data.message || "API test failed");
      }
    } catch (e) {
      setApiTestResult({
        success: false,
        message: e.response?.data?.message || "Failed to test API connection"
      });
      toast.error("Failed to test API connection");
    } finally {
      setTestingApi(false);
    }
  };

  // Account CRUD
  const handleSaveAccount = async () => {
    try {
      if (editingAccount) {
        await axios.put(`${API}/customer-accounts/${editingAccount.id}`, accountForm);
        
        // If watcher URL provided, save it separately
        if (accountForm.watcher_url) {
          await axios.put(`${API}/customer-accounts/${editingAccount.id}/watcher`, null, { 
            params: { watcher_url: accountForm.watcher_url } 
          });
        }
        toast.success("Account updated");
      } else {
        const res = await axios.post(`${API}/customer-accounts`, accountForm);
        
        // If watcher URL provided, save it separately for new account
        if (accountForm.watcher_url && res.data.id) {
          await axios.put(`${API}/customer-accounts/${res.data.id}/watcher`, null, { 
            params: { watcher_url: accountForm.watcher_url } 
          });
        }
        toast.success("Account created");
      }
      setShowAccountModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save account");
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`Delete account for ${account.username}?`)) return;
    try {
      await axios.delete(`${API}/customer-accounts/${account.id}`);
      toast.success("Account deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete account");
    }
  };

  // Maintenance log CRUD
  const handleSaveLog = async () => {
    try {
      await axios.post(`${API}/maintenance-logs`, logForm);
      toast.success("Log added");
      setShowLogModal(false);
      setLogForm({ customer_id: "", machine_info: "", description: "" });
      fetchData();
    } catch (e) {
      toast.error("Failed to add log");
    }
  };

  // Sync ViaBTC API Keys
  const handleSyncViaBtcAccounts = async () => {
    try {
      toast.info("Syncing ViaBTC sub-accounts...");
      const res = await axios.post(`${API}/viabtc/sync-accounts`);
      if (res.data.success) {
        toast.success(`Synced ${res.data.updated.length} accounts`);
        if (res.data.not_found.length > 0) {
          toast.info(`${res.data.not_found.length} ViaBTC accounts not matched: ${res.data.not_found.slice(0, 5).join(", ")}...`);
        }
        fetchData();
      } else {
        toast.error(res.data.error || "Failed to sync");
      }
    } catch (e) {
      toast.error("Failed to sync ViaBTC accounts");
    }
  };

  const handleDeleteLog = async (log) => {
    if (!window.confirm("Delete this maintenance log entry? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/maintenance-logs/${log.id}`);
      toast.success("Log deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete log");
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-fadeIn">
        {[1, 2, 3].map(i => <div key={i} className="skeleton h-32 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-panel">
      {/* ViaBTC IP whitelist alert — big banner so admin notices Render's rotating IP */}
      {viabtcHealth && viabtcHealth.has_api_key && viabtcHealth.whitelisted === false && (
        <div
          className="rounded-2xl border-2 border-red-500/60 bg-gradient-to-br from-red-500/20 to-red-600/5 p-5 sm:p-6 shadow-[0_0_40px_-10px_rgba(239,68,68,0.6)] animate-pulse"
          data-testid="viabtc-ip-banner"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="text-red-400" size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-red-400 mb-1">
                ⚠️ ViaBTC API blocked — IP not whitelisted
              </h2>
              <p className="text-sm text-gray-200 mb-3">
                Render rotated the server IP again. ViaBTC is rejecting all API calls until you whitelist the new IP.
              </p>
              <div className="bg-black/40 border border-red-500/30 rounded-xl p-3 mb-3">
                <p className="text-xs text-gray-400 mb-1">Current backend IP — copy this into ViaBTC API whitelist</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-base sm:text-lg font-mono text-red-300 font-bold break-all">
                    {viabtcHealth.server_ip || "unknown"}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/40 text-red-300 hover:bg-red-500/20 h-7 text-xs"
                    onClick={() => {
                      navigator.clipboard.writeText(viabtcHealth.server_ip || "");
                      toast.success(`Copied ${viabtcHealth.server_ip}`);
                    }}
                    data-testid="btn-copy-ip"
                  >
                    <Copy size={12} className="mr-1" /> Copy IP
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/40 text-red-300 hover:bg-red-500/20 h-7 text-xs"
                    onClick={fetchViabtcHealth}
                    data-testid="btn-recheck-ip"
                  >
                    <RefreshCw size={12} className="mr-1" /> Re-check
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                <b>How to fix:</b> ViaBTC dashboard → Account → API → edit your API key →
                add the IP above to the whitelist → save. The banner will disappear within 5 minutes,
                or click <i>Re-check</i>.
              </p>
              {viabtcHealth.error && (
                <p className="text-[11px] text-red-300/80 mt-2 font-mono">
                  Detail: {viabtcHealth.error}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Machines & Repairs</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">All machine, repair, ViaBTC and WhatsApp tools in one place</p>
        </div>
      </div>

      {/* Backup & Restore — full database backup/restore + automatic WhatsApp delivery every 48h */}
      <div className="card p-4 sm:p-6 border-2 border-[#00E054]/30" data-testid="backup-restore-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-[#00E054]/15 flex items-center justify-center">
            <Database className="text-[#00E054]" size={20} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white">Backup & Restore</h2>
            <p className="text-xs sm:text-sm text-gray-500">Everything — customers, payments, receipts, repairs, claims, settings — in one file.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:flex gap-2 mb-4">
          <input
            type="file"
            id="admin-backup-import"
            accept=".xlsx"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              const mode = window.confirm(
                "Choose import mode:\n\nOK = MERGE (add to existing data, update duplicates)\nCancel = REPLACE (wipe everything and restore from backup)"
              ) ? "merge" : "replace";
              if (mode === "replace" && !window.confirm("WARNING: This will DELETE all existing data and replace with backup. Are you sure?")) {
                e.target.value = "";
                return;
              }
              const formData = new FormData();
              formData.append("file", file);
              toast.info("Importing backup...");
              try {
                const res = await axios.post(`${API}/import/full-backup?mode=${mode}`, formData);
                if (res.data.success) {
                  const r = res.data.results;
                  const summary = Object.entries(r).filter(([k]) => k !== "mode").map(([k, v]) => `${k}: ${v}`).join(", ");
                  toast.success(`Import complete (${r.mode}): ${summary}`);
                  fetchData();
                } else {
                  toast.error(`Import failed: ${res.data.error}`);
                }
              } catch (err) {
                toast.error("Import failed: " + (err.response?.data?.detail || err.message));
              }
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            onClick={() => {
              toast.info("Generating backup...");
              window.open(`${API}/export/full-backup?admin_token=${encodeURIComponent(getAdminToken())}`, '_blank');
            }}
            className="border-[#27272A] hover:bg-[#27272A] text-white"
            data-testid="admin-export-backup-btn"
          >
            <Download size={16} className="mr-2" /> Export Full Backup
          </Button>
          <Button
            variant="outline"
            onClick={() => document.getElementById("admin-backup-import").click()}
            className="border-[#27272A] hover:bg-[#27272A] text-white"
            data-testid="admin-import-backup-btn"
          >
            <Upload size={16} className="mr-2" /> Import Backup
          </Button>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-[#27272A]">
          <div className="min-w-0">
            <p className="text-sm text-white font-medium">Auto-send backup on WhatsApp every 48h</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {botSettings.last_auto_backup
                ? `Last sent: ${new Date(botSettings.last_auto_backup).toLocaleString()}`
                : "Not sent yet"} · to {botSettings.admin_phone || "admin phone"}
            </p>
          </div>
          <button
            onClick={async () => {
              const newVal = !botSettings.auto_backup_enabled;
              try {
                await axios.put(`${API}/bot-settings`, { auto_backup_enabled: newVal });
                setBotSettings({ ...botSettings, auto_backup_enabled: newVal });
                toast.success(newVal ? "Auto-backup enabled" : "Auto-backup disabled");
              } catch {
                toast.error("Failed to update setting");
              }
            }}
            className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${botSettings.auto_backup_enabled ? 'bg-[#25D366]' : 'bg-[#27272A]'}`}
            data-testid="toggle-auto-backup"
          >
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${botSettings.auto_backup_enabled ? 'left-7' : 'left-1'}`} />
          </button>
        </div>
      </div>

      <Tabs defaultValue="accounts" className="w-full">
        <TabsList className="w-full sm:grid sm:grid-cols-5 flex overflow-x-auto no-scrollbar justify-start sm:justify-center bg-[#0F0F0F] border border-[#27272A] p-1 h-auto gap-1">
          <TabsTrigger value="accounts" className="flex-shrink-0 data-[state=active]:bg-[#00E054] data-[state=active]:text-black text-gray-300 text-xs sm:text-sm">Accounts</TabsTrigger>
          <TabsTrigger value="machines" className="flex-shrink-0 data-[state=active]:bg-[#00E054] data-[state=active]:text-black text-gray-300 text-xs sm:text-sm">Machine Status</TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex-shrink-0 data-[state=active]:bg-[#00E054] data-[state=active]:text-black text-gray-300 text-xs sm:text-sm">WhatsApp</TabsTrigger>
          <TabsTrigger value="repairs" className="flex-shrink-0 data-[state=active]:bg-[#00E054] data-[state=active]:text-black text-gray-300 text-xs sm:text-sm">Repairs & Logs</TabsTrigger>
          <TabsTrigger value="viabtc" className="flex-shrink-0 data-[state=active]:bg-[#00E054] data-[state=active]:text-black text-gray-300 text-xs sm:text-sm">ViaBTC API</TabsTrigger>
        </TabsList>

        <TabsContent value="machines" className="space-y-6 mt-6">
      {/* Farm Stats Control */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Server className="text-[#00E054]" size={20} />
          <h2 className="text-lg font-bold text-white">Farm Stats (Display Numbers)</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">These numbers are shown to customers on their dashboard</p>
        <p className="text-xs text-gray-600 mb-4">
          These are manual "curb appeal" numbers you set by hand (with small random day-to-day variation), NOT live per-machine data —
          real-time machine status lives on the Dashboard's Live Machine Control Panel.
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label>Machines Online</Label>
            <Input
              type="number"
              value={farmStats.machines_online}
              onChange={(e) => setFarmStats({ ...farmStats, machines_online: parseInt(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              data-testid="machines-online-input"
            />
          </div>
          <div>
            <Label>Machines Offline</Label>
            <Input
              type="number"
              value={farmStats.machines_offline}
              onChange={(e) => setFarmStats({ ...farmStats, machines_offline: parseInt(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              data-testid="machines-offline-input"
            />
          </div>
          <div>
            <Label>Total Hashrate (Legacy - single value)</Label>
            <Input
              value={farmStats.total_hashrate}
              onChange={(e) => setFarmStats({ ...farmStats, total_hashrate: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              placeholder="850 TH/s"
              data-testid="hashrate-input"
            />
            <p className="text-xs text-gray-500 mt-1">Used only if per-coin field is empty</p>
          </div>
          <div className="md:col-span-2">
            <Label>Hashrate by Coin</Label>
            <Input
              value={farmStats.total_hashrate_by_coin || ""}
              onChange={(e) => setFarmStats({ ...farmStats, total_hashrate_by_coin: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              placeholder="LTC:500 GH/s, KAS:350 TH/s"
              data-testid="hashrate-by-coin-input"
            />
            <p className="text-xs text-gray-500 mt-1">Format: LTC:500 GH/s, KAS:350 TH/s (shows each coin separately on dashboard)</p>
          </div>
          <div>
            <Label>Fluctuation (±)</Label>
            <Input
              type="number"
              value={farmStats.fluctuation}
              onChange={(e) => setFarmStats({ ...farmStats, fluctuation: parseInt(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              data-testid="fluctuation-input"
            />
          </div>
          <div>
            <Label>Zcash Machines (count)</Label>
            <Input
              type="number"
              value={farmStats.zec_machines || 0}
              onChange={(e) => setFarmStats({ ...farmStats, zec_machines: parseInt(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              placeholder="0"
              data-testid="zec-machines-input"
            />
            <p className="text-xs text-gray-500 mt-1">Shows a Zcash row on the customer Farm Status</p>
          </div>
          <div>
            <Label>Zcash Hashrate (Sol/s)</Label>
            <Input
              value={farmStats.zec_hashrate || ""}
              onChange={(e) => setFarmStats({ ...farmStats, zec_hashrate: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              placeholder="1.20 GSol/s"
              data-testid="zec-hashrate-input"
            />
            <p className="text-xs text-gray-500 mt-1">Type the number with its unit, e.g. 1.20 GSol/s</p>
          </div>
        </div>
        
        <Button onClick={handleSaveFarmStats} className="mt-4 bg-[#00E054] text-black" data-testid="save-farm-stats-btn">
          <Save size={16} className="mr-2" />
          Save Farm Stats
        </Button>
      </div>

      {/* Machine Reliability History */}
      <div className="card p-4 sm:p-6" data-testid="machine-reliability-card">
        <div className="page-header mb-4">
          <div className="flex items-center gap-2">
            <Activity className="text-[#00E054]" size={20} />
            <h2 className="text-lg font-bold text-white">Machine Reliability</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1">
              {[7, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => { setReliabilityDays(d); fetchReliability(d); }}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${reliabilityDays === d ? 'bg-[#00E054] text-black' : 'text-gray-400'}`}
                  data-testid={`reliability-range-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <Button variant="outline" size="icon" onClick={() => fetchReliability()} className="border-[#27272A]" data-testid="refresh-reliability-btn">
              <RefreshCw size={16} className={loadingReliability ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Which machines went offline the most, and how long they stayed down — an early sign of a machine slowly degrading rather than one that just suddenly failed.
        </p>

        <div className="chart-container mb-6">
          {farmTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={farmTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" stroke="#A1A1AA" fontSize={11} tickFormatter={(d) => (d || "").slice(5)} />
                <YAxis stroke="#A1A1AA" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: "#0A0A0A", border: "1px solid #27272A", borderRadius: 8 }} labelStyle={{ color: "#fff" }} />
                <Bar dataKey="machines_offline" name="Machines offline" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-500 text-sm">No offline events recorded yet</div>
          )}
        </div>

        {reliabilityMachines.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">No machine downtime recorded in the last {reliabilityDays} days 🎉</div>
        ) : (
          <>
            <div className="data-table-wrap overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="table-header">
                    <th className="text-left py-3 px-4">Machine</th>
                    <th className="text-left py-3 px-4">Customer</th>
                    <th className="text-right py-3 px-4">Offline events</th>
                    <th className="text-right py-3 px-4">Total downtime</th>
                    <th className="text-right py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reliabilityMachines.map((m) => (
                    <tr key={m.worker_id} className="table-row">
                      <td className="py-3 px-4 text-white font-mono text-sm">{m.worker_name}</td>
                      <td className="py-3 px-4 text-gray-400">{m.account}</td>
                      <td className="py-3 px-4 text-right text-white">{m.offline_count}</td>
                      <td className="py-3 px-4 text-right text-gray-400">{formatDowntime(m.total_downtime_minutes)}</td>
                      <td className="py-3 px-4 text-right">
                        {m.currently_offline ? (
                          <span className="text-red-400 text-xs font-medium">● Offline</span>
                        ) : (
                          <span className="text-[#00E054] text-xs font-medium">● Online</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="data-cards">
              {reliabilityMachines.map((m) => (
                <div key={m.worker_id} className="data-card">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-white font-semibold font-mono text-sm truncate">{m.worker_name}</span>
                    {m.currently_offline ? (
                      <span className="text-red-400 text-xs font-medium shrink-0">● Offline</span>
                    ) : (
                      <span className="text-[#00E054] text-xs font-medium shrink-0">● Online</span>
                    )}
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">Customer</span>
                    <span className="text-gray-300 text-sm">{m.account}</span>
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">Offline events</span>
                    <span className="text-white text-sm">{m.offline_count}</span>
                  </div>
                  <div className="data-card-row">
                    <span className="data-card-label">Total downtime</span>
                    <span className="text-gray-300 text-sm">{formatDowntime(m.total_downtime_minutes)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Monthly Offline Hours */}
        <div className="mt-6 pt-6 border-t border-[#27272A]">
          <div className="page-header mb-4">
            <div>
              <h3 className="text-sm font-bold text-white">Monthly Offline Hours</h3>
              <p className="text-xs text-gray-500 mt-1">How many hours each machine was offline in the selected month</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="month"
                value={selectedMonth}
                max={new Date().toISOString().slice(0, 7)}
                onChange={(e) => { setSelectedMonth(e.target.value); fetchMonthlyDowntime(e.target.value); }}
                className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-1.5 text-sm text-white"
                data-testid="monthly-downtime-month-input"
              />
              <Button variant="outline" size="icon" onClick={() => fetchMonthlyDowntime()} className="border-[#27272A]" data-testid="refresh-monthly-btn">
                <RefreshCw size={16} className={loadingMonthly ? "animate-spin" : ""} />
              </Button>
            </div>
          </div>

          {monthlyMachines.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No downtime recorded for {selectedMonth} 🎉</div>
          ) : (
            <>
              <div className="data-table-wrap overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="text-left py-3 px-4">Machine</th>
                      <th className="text-left py-3 px-4">Customer</th>
                      <th className="text-right py-3 px-4">Offline events</th>
                      <th className="text-right py-3 px-4">Offline hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyMachines.map((m) => (
                      <tr key={m.worker_id} className="table-row">
                        <td className="py-3 px-4 text-white font-mono text-sm">{m.worker_name}</td>
                        <td className="py-3 px-4 text-gray-400">{m.account}</td>
                        <td className="py-3 px-4 text-right text-white">{m.offline_events}</td>
                        <td className="py-3 px-4 text-right text-gray-400">{m.offline_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="data-cards">
                {monthlyMachines.map((m) => (
                  <div key={m.worker_id} className="data-card">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-white font-semibold font-mono text-sm truncate">{m.worker_name}</span>
                      <span className="text-gray-300 text-xs font-medium shrink-0">{m.offline_hours}h</span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label">Customer</span>
                      <span className="text-gray-300 text-sm">{m.account}</span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label">Offline events</span>
                      <span className="text-white text-sm">{m.offline_events}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

        </TabsContent>

        <TabsContent value="viabtc" className="space-y-6 mt-6">
      {/* ViaBTC API Settings */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="text-[#F59E0B]" size={20} />
          <h2 className="text-lg font-bold text-white">ViaBTC API Settings</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Connect to ViaBTC to fetch live worker status and hashrate for customers</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Access Key</Label>
            <Input
              value={viaBtcSettings.access_key}
              onChange={(e) => setViaBtcSettings({ ...viaBtcSettings, access_key: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] mt-1"
              placeholder="Enter your ViaBTC Access Key"
              data-testid="viabtc-access-key"
            />
          </div>
          <div>
            <Label>Secret Key</Label>
            <div className="relative">
              <Input
                type={showSecretKey ? "text" : "password"}
                value={viaBtcSettings.secret_key}
                onChange={(e) => setViaBtcSettings({ ...viaBtcSettings, secret_key: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] mt-1 pr-10"
                placeholder="Enter your ViaBTC Secret Key"
                data-testid="viabtc-secret-key"
              />
              <button
                type="button"
                onClick={() => setShowSecretKey(!showSecretKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              >
                {showSecretKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={viaBtcSettings.enabled}
              onChange={(e) => setViaBtcSettings({ ...viaBtcSettings, enabled: e.target.checked })}
              className="w-4 h-4 rounded border-[#27272A] bg-[#0A0A0A] text-[#00E054] focus:ring-[#00E054]"
            />
            <span className="text-sm text-gray-400">Enable ViaBTC Integration</span>
          </label>
        </div>

        <div className="flex flex-col gap-3 mt-4">
          <div className="btn-row">
            <Button onClick={handleSaveViaBtcSettings} className="bg-[#F59E0B] text-black" data-testid="save-viabtc-btn">
              <Save size={16} className="mr-2" />
              Save API Settings
            </Button>
            <Button
              onClick={handleTestViaBtcApi}
              variant="outline"
              className="border-[#27272A]"
              disabled={testingApi || !viaBtcSettings.access_key || !viaBtcSettings.secret_key}
              data-testid="test-viabtc-btn"
            >
              {testingApi ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Activity size={16} className="mr-2" />
                  Test Connection
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500">Get your API keys from <a href="https://www.viabtc.com/tools/api" target="_blank" rel="noopener noreferrer" className="text-[#00C2FF] hover:underline">ViaBTC API Settings</a></p>
        </div>

        <OkxTradeAlerts />
        <MarketPosts />

        {/* F2Pool API Settings */}
        <div className="bg-[#111113] border border-[#27272A] rounded-2xl p-4 sm:p-6 space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">F2Pool API</h2>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input type="checkbox" checked={f2poolSettings.enabled}
                     onChange={(e) => setF2poolSettings({ ...f2poolSettings, enabled: e.target.checked })}
                     data-testid="f2pool-enabled" />
              Enabled
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Reads your F2Pool workers into the dashboards, WhatsApp status and reports — same as ViaBTC.
            One farm-level token for your F2Pool account.
          </p>
          <div>
            <Label className="text-xs text-gray-400">F2Pool accounts — BLANK = all, or list the ones you want (one per line, or comma-separated)</Label>
            <textarea className="w-full min-h-[80px] rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm text-white font-mono"
                   placeholder={"(blank = all accounts)\nturkbeast89\ndeeb\nwkbeast"}
                   value={f2poolSettings.account}
                   onChange={(e) => setF2poolSettings({ ...f2poolSettings, account: e.target.value })}
                   data-testid="f2pool-account" />
            <p className="text-[11px] text-gray-500 mt-1">
              Add up to ~20. Fewer accounts = faster, lighter polling. Leave blank only if you want every account F2Pool returns.
            </p>
          </div>
          <p className="text-xs text-gray-500">
            All coins you mine on F2Pool (LTC + Doge, BTC, KAS, ZEC) are pulled automatically — no need to pick one.
          </p>
          <div>
            <Label className="text-xs text-gray-400">
              API token {f2poolSettings.has_token ? "(saved — leave blank to keep it)" : ""}
            </Label>
            <Input type="password" className="bg-[#0A0A0A] border-[#27272A]"
                   placeholder={f2poolSettings.has_token ? "•••••••• (unchanged)" : "paste your F2Pool API token"}
                   value={f2poolSettings.token}
                   onChange={(e) => setF2poolSettings({ ...f2poolSettings, token: e.target.value })}
                   data-testid="f2pool-token" />
            <p className="text-[11px] text-gray-600 mt-1">
              f2pool.com → Account Settings → API Token (read-only). Add your server's outbound IPs to the token's allow-list.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveF2poolSettings} disabled={savingF2pool}
                    className="bg-[#F59E0B] text-black" data-testid="save-f2pool-btn">
              {savingF2pool ? "Saving…" : "Save F2Pool settings"}
            </Button>
            <Button onClick={handleTestF2pool} disabled={testingF2pool} variant="outline"
                    className="border-[#27272A] text-gray-200" data-testid="test-f2pool-btn">
              {testingF2pool ? "Testing…" : "Test Connection"}
            </Button>
            <Button onClick={() => handleCheckF2poolBalances(false)} disabled={loadingF2poolBalances} variant="outline"
                    className="border-[#F59E0B]/50 text-[#F59E0B]" data-testid="f2pool-balances-btn">
              {loadingF2poolBalances ? "Checking…" : "Check unpaid balances"}
            </Button>
          </div>
          <F2poolReadonlyLinks />
          {f2poolBalances && (
            f2poolBalances.enabled === false ? (
              <div className="text-xs rounded-lg p-3 border border-red-500/40 bg-red-500/10 text-red-300">
                ❌ {f2poolBalances.error || "F2Pool not enabled — save the token first."}
              </div>
            ) : (
              <div className="text-xs rounded-lg p-3 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-gray-200">
                <div className="font-bold text-[#F59E0B] mb-2">Unpaid balance (unsettled){f2poolBalances.pairs > 0 ? ` — ${f2poolBalances.parsed ?? f2poolBalances.pairs} of ${f2poolBalances.pairs} account lookups read` : ""}</div>
                {(f2poolBalances.failed > 0 || f2poolBalances.unparsed > 0) && (
                  <div className="mb-2 rounded border border-red-500/40 bg-red-500/10 text-red-300 p-2">
                    ⚠️ These totals are incomplete.
                    {f2poolBalances.failed > 0 && <div>{f2poolBalances.failed} lookup(s) failed{f2poolBalances.first_error ? ` — ${f2poolBalances.first_error}` : ""}</div>}
                    {f2poolBalances.unparsed > 0 && <div>{f2poolBalances.unparsed} reply(ies) could not be read{f2poolBalances.unparsed_keys ? ` — fields returned: ${f2poolBalances.unparsed_keys.join(", ")}` : ""}</div>}
                  </div>
                )}
                {Object.keys(f2poolBalances.coins || {}).length === 0 ? (
                  <div className="text-gray-400">{f2poolBalances.message || "No balances returned. If the F2Pool machine list is still loading, wait a few minutes and check again."}</div>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(f2poolBalances.coins).filter(([, b]) => b.unpaid || b.estimated_today || b.immature || b.paid).map(([coin, b]) => (
                      <div key={coin} className="border-t border-[#F59E0B]/20 pt-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-white">{coin}</span>
                          <span className="text-[#00E054] font-bold">{b.unpaid} {coin} unpaid</span>
                        </div>
                        <div className="text-gray-400 mt-0.5">
                          today est: {b.estimated_today} {coin}{b.immature ? ` · immature: ${b.immature} ${coin}` : ""} · paid to date: {b.paid} {coin}
                        </div>
                        {b.accounts && Object.keys(b.accounts).length > 0 && (
                          <div className="text-gray-500 mt-1">
                            by account: {Object.entries(b.accounts).sort((a, c) => c[1] - a[1]).map(([a, v]) => `${a}=${v}`).join(" · ")}
                          </div>
                        )}
                      </div>
                    ))}
                    {(() => {
                      const idle = Object.entries(f2poolBalances.coins).filter(([, b]) => !(b.unpaid || b.estimated_today || b.immature || b.paid)).map(([coin]) => coin);
                      return idle.length > 0 ? <div className="text-gray-500 border-t border-[#F59E0B]/20 pt-2">No balance or payouts on: {idle.join(", ")}</div> : null;
                    })()}
                    <div className="text-gray-600 pt-1">Balances are per-coin (different units, never summed). Cached 10 min — <button className="underline" onClick={() => handleCheckF2poolBalances(true)}>force refresh</button>.</div>
                  </div>
                )}
              </div>
            )
          )}
          {f2poolTestResult && (
            <div className={`text-xs rounded-lg p-3 border ${f2poolTestResult.success ? "border-[#00E054]/40 bg-[#00E054]/10 text-[#00E054]" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>
              {f2poolTestResult.success ? (
                <div>
                  ✅ Connected — {f2poolTestResult.accounts ? `${f2poolTestResult.accounts} account(s)` : ""}{f2poolTestResult.sample_probe ? "" : `, ${f2poolTestResult.total_workers} worker(s), ${f2poolTestResult.online} online`}.
                  {f2poolTestResult.note ? <div className="text-gray-400 mt-1">{f2poolTestResult.note}</div> : null}
                  {f2poolTestResult.sample?.length ? <div className="text-gray-400 mt-1">e.g. {f2poolTestResult.sample.join(", ")}</div> : null}
                  {f2poolTestResult.probes?.length ? <div className="text-gray-400 mt-1">probes: {f2poolTestResult.probes.map(p => `${p.account}=${p.error ? "ERR("+p.error+")" : p.workers+"w/"+(p.online||0)+"on"+(p.sample ? " ["+p.sample.name+" st="+p.sample.status+" "+p.sample.h1+"GH/h1]" : "")}`).join(" · ")}</div> : null}
                  {f2poolTestResult.detail?.length ? <div className="text-gray-400 mt-1">top: {f2poolTestResult.detail.map(d => `${d.name} st=${d.status} now=${d.now_gh}GH h1=${d.h1_gh}GH`).join(" · ")}</div> : null}
                  {f2poolTestResult.build ? <div className="text-gray-600 mt-1">build: {f2poolTestResult.build}</div> : null}
                </div>
              ) : (
                <div>❌ {f2poolTestResult.error}</div>
              )}
            </div>
          )}
        </div>

        {/* API Test Result */}
        {apiTestResult && (
          <div className={`mt-4 p-4 rounded-lg border ${apiTestResult.success ? 'bg-[#00E054]/10 border-[#00E054]/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-2 mb-2">
              {apiTestResult.success ? (
                <div className="w-3 h-3 rounded-full bg-[#00E054] animate-pulse"></div>
              ) : (
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
              )}
              <span className={`font-medium ${apiTestResult.success ? 'text-[#00E054]' : 'text-red-400'}`}>
                {apiTestResult.success ? 'Connection Successful' : 'Connection Failed'}
              </span>
            </div>
            <p className="text-sm text-gray-400">{apiTestResult.message}</p>
            {apiTestResult.server_ip && (
              <div className="mt-2 p-2 bg-[#1A1A1A] rounded border border-[#27272A]">
                <p className="text-xs text-gray-400">Add this IP to your ViaBTC whitelist:</p>
                <p className="text-lg font-mono text-[#00C2FF] mt-1">{apiTestResult.server_ip}</p>
              </div>
            )}
            {apiTestResult.error && !apiTestResult.server_ip && (
              <p className="text-xs text-red-400 mt-1">Error: {apiTestResult.error}</p>
            )}
            {apiTestResult.success && apiTestResult.data && (
              <div className="mt-2 text-xs text-gray-500">
                <p>Hashrate data received successfully</p>
              </div>
            )}
          </div>
        )}

        {/* Watcher Link (Alternative to API) */}
        <div className="mt-6 p-4 bg-[#1A1A1A] rounded-lg border border-[#00E054]/30">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="text-[#00E054]" size={18} />
            <h3 className="font-medium text-[#00E054]">Watcher Link (No IP Whitelist Needed)</h3>
          </div>
          <p className="text-xs text-gray-500 mb-3">Use watcher links instead of API keys - no IP whitelisting required!</p>
          
          <div className="space-y-2">
            <Label className="text-gray-400">Main Account Watcher URL</Label>
            <div className="flex gap-2">
              <Input
                value={mainWatcherUrl}
                onChange={e => setMainWatcherUrl(e.target.value)}
                placeholder="https://www.viabtc.com/en/observer/worker?access_key=xxx&coin=LTC"
                className="flex-1 bg-[#0A0A0A] border-[#27272A] text-gray-300"
              />
              <Button 
                onClick={async () => {
                  if (!mainWatcherUrl) {
                    toast.error("Enter a watcher URL");
                    return;
                  }
                  setSavingWatcher(true);
                  try {
                    await axios.put(`${API}/viabtc-watcher`, null, { params: { watcher_url: mainWatcherUrl } });
                    toast.success("Main account watcher saved!");
                    fetchData();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to save watcher");
                  } finally {
                    setSavingWatcher(false);
                  }
                }}
                disabled={savingWatcher}
                className="bg-[#00E054] text-black"
              >
                {savingWatcher ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
              </Button>
            </div>
            <p className="text-xs text-gray-500">Get watcher link from ViaBTC → Observer → Copy Link</p>
          </div>
        </div>
      </div>

        </TabsContent>

        <TabsContent value="accounts" className="space-y-6 mt-6">
      {/* Pending signup requests (customer self-signup) */}
      {signupRequests.length > 0 && (
        <div className="card p-4 sm:p-6 border border-[#00E054]/30">
          <div className="flex items-center gap-2 mb-4">
            <Users className="text-[#00E054]" size={20} />
            <h2 className="text-lg font-bold text-white">Signup Requests</h2>
            <span className="px-2 py-0.5 rounded-full bg-[#00E054]/15 text-[#00E054] text-xs font-bold">
              {signupRequests.length} pending
            </span>
          </div>
          <div className="space-y-3">
            {signupRequests.map((req) => (
              <div key={req.id} className="p-3 bg-[#0F0F0F] border border-[#27272A] rounded-lg">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="text-white font-bold">{req.name}</span>
                  <span className="text-gray-400">{req.phone}</span>
                  <span className="text-[#00C2FF]">@{req.username}</span>
                  <span className="text-gray-600 text-xs">{(req.created_at || "").slice(0, 10)}</span>
                </div>
                {req.note && (
                  <p className="text-gray-500 text-xs mt-1 italic">"{req.note}"</p>
                )}

                {/* Optional one-shot linking: the machine they bought */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                  {viabtcWorkers.length > 0 ? (
                    <select
                      value={(signupAssign[req.id] || {}).worker_name || ""}
                      onChange={(e) => setAssign(req.id, { worker_name: e.target.value })}
                      className="px-3 py-2 bg-[#050505] border border-[#27272A] rounded-lg text-white text-xs focus:outline-none focus:border-[#00E054]/50"
                      data-testid={`signup-worker-select-${req.id}`}
                    >
                      <option value="">ViaBTC worker: none (link later)</option>
                      {viabtcWorkers.map((w) => (
                        <option key={w.worker_name} value={w.worker_name}>
                          {w.worker_name} {w.worker_status === "active" ? "· online" : "· offline"}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={(signupAssign[req.id] || {}).worker_name || ""}
                      onChange={(e) => setAssign(req.id, { worker_name: e.target.value })}
                      placeholder="ViaBTC worker name (optional, e.g. aliwk)"
                      className="px-3 py-2 bg-[#050505] border border-[#27272A] rounded-lg text-white text-xs focus:outline-none focus:border-[#00E054]/50"
                      data-testid={`signup-worker-input-${req.id}`}
                    />
                  )}
                  <select
                    value={(signupAssign[req.id] || {}).machine_type_id || ""}
                    onChange={(e) => {
                      const mt = machineTypes.find((m) => m.id === e.target.value);
                      const qty = parseInt((signupAssign[req.id] || {}).quantity) || 1;
                      setAssign(req.id, {
                        machine_type_id: e.target.value,
                        monthly_fee: mt ? String((mt.monthly_fee || 0) * qty) : "",
                      });
                    }}
                    className="px-3 py-2 bg-[#050505] border border-[#27272A] rounded-lg text-white text-xs focus:outline-none focus:border-[#00E054]/50"
                    data-testid={`signup-machine-select-${req.id}`}
                  >
                    <option value="">Machine / billing: none (set later)</option>
                    {machineTypes.map((mt) => (
                      <option key={mt.id} value={mt.id}>
                        {mt.name} (${mt.monthly_fee}/mo)
                      </option>
                    ))}
                  </select>
                  {(signupAssign[req.id] || {}).machine_type_id && (
                    <div className="flex items-center gap-2 sm:col-span-2">
                      <label className="text-gray-500 text-xs">Qty</label>
                      <input
                        type="number"
                        min="1"
                        value={(signupAssign[req.id] || {}).quantity || "1"}
                        onChange={(e) => {
                          const qty = parseInt(e.target.value) || 1;
                          const a = signupAssign[req.id] || {};
                          const mt = machineTypes.find((m) => m.id === a.machine_type_id);
                          setAssign(req.id, {
                            quantity: e.target.value,
                            monthly_fee: mt ? String((mt.monthly_fee || 0) * qty) : a.monthly_fee,
                          });
                        }}
                        className="w-16 px-2 py-2 bg-[#050505] border border-[#27272A] rounded-lg text-white text-xs focus:outline-none"
                        data-testid={`signup-qty-${req.id}`}
                      />
                      <label className="text-gray-500 text-xs">Monthly fee $</label>
                      <input
                        type="number"
                        min="0"
                        value={(signupAssign[req.id] || {}).monthly_fee ?? ""}
                        onChange={(e) => setAssign(req.id, { monthly_fee: e.target.value })}
                        className="w-24 px-2 py-2 bg-[#050505] border border-[#27272A] rounded-lg text-white text-xs focus:outline-none"
                        data-testid={`signup-fee-${req.id}`}
                      />
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <select
                    value={signupLinkChoice[req.id] || ""}
                    onChange={(e) =>
                      setSignupLinkChoice({ ...signupLinkChoice, [req.id]: e.target.value })
                    }
                    className="flex-1 min-w-[180px] px-3 py-2 bg-[#050505] border border-[#27272A] rounded-lg text-white text-xs focus:outline-none focus:border-[#00E054]/50"
                    data-testid={`signup-link-select-${req.id}`}
                  >
                    <option value="">+ Create as NEW customer</option>
                    {customers
                      .filter((c) => !accounts.some((a) => a.customer_id === c.id))
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          Link to existing: {c.name}
                        </option>
                      ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={() => handleApproveSignup(req)}
                    className="bg-[#00E054] text-black text-xs font-bold"
                    data-testid={`signup-approve-${req.id}`}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRejectSignup(req)}
                    className="border-red-500/40 text-red-400 text-xs hover:bg-red-500/10"
                    data-testid={`signup-reject-${req.id}`}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customer Accounts */}
      <div className="card p-4 sm:p-6">
        <div className="page-header mb-4">
          <div className="flex items-center gap-2">
            <Key className="text-[#00C2FF]" size={20} />
            <h2 className="text-lg font-bold text-white">Customer Accounts</h2>
          </div>
          <div className="btn-row">
            <Button onClick={handleSyncViaBtcAccounts} variant="outline" className="border-[#F59E0B] text-[#F59E0B]" title="Fetches API keys from ViaBTC for accounts whose Worker Name matches a ViaBTC sub-account" data-testid="sync-viabtc-btn">
              <RefreshCw size={16} className="mr-2" />
              <span className="truncate">Sync ViaBTC Keys</span>
            </Button>
            <Button onClick={handleAutoCreateAccounts} variant="outline" className="border-[#27272A]" title="Creates a login for every customer on your Customers page who doesn't have one yet. Does NOT read from ViaBTC — new ViaBTC workers need a customer added first." data-testid="auto-create-btn">
              <RefreshCw size={16} className="mr-2" />
              <span className="truncate">Create Logins for Customers</span>
            </Button>
            <Button onClick={() => { setEditingAccount(null); setAccountForm({ customer_id: "", username: "", password: "", worker_name: "", viabtc_api_key: "" }); setShowAccountModal(true); }} className="bg-[#00E054] text-black col-span-2 sm:col-span-1" data-testid="add-account-btn">
              <Plus size={16} className="mr-2" />
              Add Account
            </Button>
          </div>
        </div>

        {/* Desktop / tablet: full table */}
        <div className="data-table-wrap overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="text-left py-3 px-4">Customer</th>
                <th className="text-left py-3 px-4">Username / Worker Name</th>
                <th className="text-left py-3 px-4">Password</th>
                <th className="text-left py-3 px-4">API Key</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(account => (
                <tr key={account.id} className="table-row">
                  <td className="py-3 px-4 text-white">{getCustomerName(account.customer_id)}</td>
                  <td className="py-3 px-4 text-[#00C2FF] font-mono">{account.worker_name || account.username || <span className="text-red-400">Not set</span>}</td>
                  <td className="py-3 px-4 font-mono text-gray-400">{account.password}</td>
                  <td className="py-3 px-4 font-mono text-xs">
                    {account.watcher_key ? (
                      <span className="text-[#00E054]" title="Watcher">👁 {account.watcher_key.substring(0, 8)}...</span>
                    ) : account.viabtc_api_key ? (
                      <span className="text-yellow-400" title="API Key">{account.viabtc_api_key.substring(0, 8)}...</span>
                    ) : (
                      <span className="text-red-400">Not configured</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={async () => {
                        const cust = customers.find(c => c.id === account.customer_id);
                        if (!cust) return;
                        try {
                          const res = await axios.put(`${API}/customers/${account.customer_id}/whatsapp-toggle`);
                          toast.success(`WhatsApp ${res.data.whatsapp_enabled ? 'ON' : 'OFF'} for ${res.data.name}`);
                          fetchData();
                        } catch (e) { toast.error("Failed"); }
                      }}
                      className={`w-8 h-4 rounded-full relative inline-block mr-2 transition-colors ${
                        (customers.find(c => c.id === account.customer_id)?.whatsapp_enabled !== false) ? 'bg-[#25D366]' : 'bg-[#27272A]'
                      }`}
                      title={`WhatsApp ${(customers.find(c => c.id === account.customer_id)?.whatsapp_enabled !== false) ? 'ON' : 'OFF'}`}
                      data-testid={`wa-toggle-${account.worker_name}`}
                    >
                      <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                        (customers.find(c => c.id === account.customer_id)?.whatsapp_enabled !== false) ? 'left-4' : 'left-0.5'
                      }`} />
                    </button>
                    <Button size="icon" variant="ghost" onClick={() => {
                      setEditingAccount(account);
                      setAccountForm({
                        customer_id: account.customer_id,
                        username: account.username,
                        password: account.password,
                        worker_name: account.worker_name || "",
                        viabtc_api_key: account.viabtc_api_key || "",
                        watcher_url: account.watcher_key ? `https://www.viabtc.com/en/observer/worker?access_key=${account.watcher_key}&coin=LTC` : ""
                      });
                      setShowAccountModal(true);
                    }}>
                      <Edit2 size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-[#00E054]" title="Send login details via WhatsApp" onClick={() => handleSendWelcome(account)} data-testid={`send-welcome-${account.id}`}>
                      <Send size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDeleteAccount(account)}>
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No accounts yet. Click "Auto-Create All" to generate accounts for all customers.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards, one account per card */}
        <div className="data-cards">
          {accounts.map(account => {
            const cust = customers.find(c => c.id === account.customer_id);
            const waOn = cust?.whatsapp_enabled !== false;
            return (
              <div key={account.id} className="data-card">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-white font-semibold truncate">{getCustomerName(account.customer_id)}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => {
                      setEditingAccount(account);
                      setAccountForm({
                        customer_id: account.customer_id,
                        username: account.username,
                        password: account.password,
                        worker_name: account.worker_name || "",
                        viabtc_api_key: account.viabtc_api_key || "",
                        watcher_url: account.watcher_key ? `https://www.viabtc.com/en/observer/worker?access_key=${account.watcher_key}&coin=LTC` : ""
                      });
                      setShowAccountModal(true);
                    }}>
                      <Edit2 size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-[#00E054]" title="Send login details via WhatsApp" onClick={() => handleSendWelcome(account)}>
                      <Send size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDeleteAccount(account)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                <div className="data-card-row">
                  <span className="data-card-label">Username / Worker</span>
                  <span className="text-[#00C2FF] font-mono text-sm">{account.worker_name || account.username || <span className="text-red-400">Not set</span>}</span>
                </div>
                <div className="data-card-row">
                  <span className="data-card-label">Password</span>
                  <span className="font-mono text-gray-300 text-sm">{account.password}</span>
                </div>
                <div className="data-card-row">
                  <span className="data-card-label">API Key</span>
                  <span className="font-mono text-xs">
                    {account.watcher_key ? (
                      <span className="text-[#00E054]">👁 {account.watcher_key.substring(0, 8)}...</span>
                    ) : account.viabtc_api_key ? (
                      <span className="text-yellow-400">{account.viabtc_api_key.substring(0, 8)}...</span>
                    ) : (
                      <span className="text-red-400">Not configured</span>
                    )}
                  </span>
                </div>
                <div className="data-card-row">
                  <span className="data-card-label">WhatsApp</span>
                  <button
                    onClick={async () => {
                      if (!cust) return;
                      try {
                        const res = await axios.put(`${API}/customers/${account.customer_id}/whatsapp-toggle`);
                        toast.success(`WhatsApp ${res.data.whatsapp_enabled ? 'ON' : 'OFF'} for ${res.data.name}`);
                        fetchData();
                      } catch (e) { toast.error("Failed"); }
                    }}
                    className={`w-10 h-5 rounded-full relative inline-block transition-colors ${waOn ? 'bg-[#25D366]' : 'bg-[#27272A]'}`}
                    title={`WhatsApp ${waOn ? 'ON' : 'OFF'}`}
                    data-testid={`wa-toggle-mobile-${account.worker_name}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${waOn ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>
              </div>
            );
          })}
          {accounts.length === 0 && (
            <p className="py-8 text-center text-gray-500 text-sm">
              No accounts yet. Tap "Add Account" or "Create Logins for Customers" to generate logins for everyone in your Customers list.
            </p>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-3">
          <span className="text-gray-400">"Create Logins for Customers"</span> only looks at your Customers page — it does NOT pull new workers from ViaBTC. Made a new worker on ViaBTC? Add the customer on the Customers page first, then create their account here (or use the machine dropdown when approving a signup above).
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Tap <span className="text-[#F59E0B]">"Sync ViaBTC Keys"</span> to auto-fetch API keys for accounts where Worker Name matches a ViaBTC sub-account.
        </p>
      </div>

      {/* Helper Accounts — view-only staff logins */}
      <div className="card p-4 sm:p-6" data-testid="helper-accounts-card">
        <div className="page-header mb-4">
          <div className="flex items-center gap-2">
            <Eye className="text-[#00C2FF]" size={20} />
            <h2 className="text-lg font-bold text-white">Helper Accounts (View-Only)</h2>
          </div>
          <Button
            onClick={() => { setHelperForm({ username: "", password: "", display_name: "" }); setShowHelperModal(true); }}
            className="bg-[#00C2FF] text-black w-full sm:w-auto"
            data-testid="add-helper-btn"
          >
            <Plus size={16} className="mr-2" />
            Add Helper
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Staff logins that can only monitor machine online/offline status and IPs at <span className="text-[#00C2FF]">wkbeast.org/helper</span> — they cannot see payments, customer data, or change anything.
        </p>

        <div className="data-table-wrap overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="text-left py-3 px-4">Username</th>
                <th className="text-left py-3 px-4">Display Name</th>
                <th className="text-right py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {helperAccounts.map((h) => (
                <tr key={h.id} className="table-row">
                  <td className="py-3 px-4 text-white font-mono">{h.username}</td>
                  <td className="py-3 px-4 text-gray-400">{h.display_name || "—"}</td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => handleToggleHelperActive(h)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${h.active ? 'bg-[#25D366]' : 'bg-[#27272A]'}`}
                      data-testid={`toggle-helper-active-${h.username}`}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${h.active ? 'left-6' : 'left-1'}`} />
                    </button>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleResetHelperPassword(h)} className="border-[#27272A] text-xs">
                        Reset Password
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDeleteHelper(h)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="data-cards">
          {helperAccounts.map((h) => (
            <div key={h.id} className="data-card">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-white font-semibold font-mono text-sm truncate">{h.username}</span>
                <button
                  onClick={() => handleToggleHelperActive(h)}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${h.active ? 'bg-[#25D366]' : 'bg-[#27272A]'}`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${h.active ? 'left-6' : 'left-1'}`} />
                </button>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Display Name</span>
                <span className="text-gray-300 text-sm">{h.display_name || "—"}</span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => handleResetHelperPassword(h)} className="border-[#27272A] text-xs flex-1">
                  Reset Password
                </Button>
                <Button size="icon" variant="ghost" onClick={() => handleDeleteHelper(h)} className="text-red-400 hover:text-red-300 shrink-0">
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
        {helperAccounts.length === 0 && (
          <p className="py-8 text-center text-gray-500 text-sm">No helper accounts yet. Tap "Add Helper" to create one.</p>
        )}
      </div>

        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-6 mt-6">
      {/* WhatsApp Bot Automation */}
      <div className="card p-4 sm:p-6" data-testid="whatsapp-bot-settings">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="text-[#25D366]" size={20} />
          <h2 className="text-lg font-bold text-white">WhatsApp Bot Automation</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Auto Payment Reminders */}
          <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-white">Auto Payment Reminders</h3>
                <p className="text-xs text-gray-500 mt-1">Sends on 1st of month + every {botSettings.reminder_interval_days} days if unpaid</p>
              </div>
              <button
                onClick={async () => {
                  const newVal = !botSettings.auto_reminders_enabled;
                  try {
                    await axios.put(`${API}/bot-settings`, { auto_reminders_enabled: newVal });
                    setBotSettings({ ...botSettings, auto_reminders_enabled: newVal });
                    toast.success(newVal ? "Auto reminders ON" : "Auto reminders OFF");
                  } catch (e) { toast.error("Failed to update"); }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors ${botSettings.auto_reminders_enabled ? 'bg-[#25D366]' : 'bg-[#27272A]'}`}
                data-testid="toggle-auto-reminders"
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${botSettings.auto_reminders_enabled ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
            <div className="text-xs text-gray-500">
              {botSettings.last_reminder_sent ? `Last sent: ${new Date(botSettings.last_reminder_sent).toLocaleDateString()}` : "Never sent yet"}
            </div>
            <div className="mt-3 flex items-center justify-between p-2 bg-[#1A1A1A] rounded">
              <div className="flex-1">
                <p className="text-xs font-semibold text-white">📄 Attach PDF invoice</p>
                <p className="text-[10px] text-gray-500">Sends a styled PDF with each reminder</p>
              </div>
              <button
                onClick={async () => {
                  const newVal = !botSettings.attach_pdf_to_reminders;
                  try {
                    await axios.put(`${API}/bot-settings`, { attach_pdf_to_reminders: newVal });
                    setBotSettings({ ...botSettings, attach_pdf_to_reminders: newVal });
                    toast.success(newVal ? "PDF attachments ON" : "PDF attachments OFF");
                  } catch (e) { toast.error("Failed to update"); }
                }}
                className={`relative w-10 h-5 rounded-full transition-colors ${botSettings.attach_pdf_to_reminders ? 'bg-[#FFD700]' : 'bg-[#27272A]'}`}
                data-testid="toggle-attach-pdf"
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${botSettings.attach_pdf_to_reminders ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 border-[#27272A] text-xs"
              onClick={async () => {
                toast.info("Sending reminders...");
                try {
                  const res = await axios.post(`${API}/bot/run-reminder-check`);
                  if (res.data.success) toast.success(`Sent ${res.data.sent} reminders!`);
                  else toast.error(res.data.message || res.data.error);
                } catch (e) { toast.error("Failed"); }
              }}
              data-testid="manual-send-reminders"
            >
              Send Now
            </Button>
          </div>
          
          {/* Offline Machine Alerts */}
          <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-white">Offline Machine Alerts</h3>
                <p className="text-xs text-gray-500 mt-1">WhatsApp alert when machine goes offline/online</p>
              </div>
              <button
                onClick={async () => {
                  const newVal = !botSettings.offline_alerts_enabled;
                  try {
                    await axios.put(`${API}/bot-settings`, { offline_alerts_enabled: newVal });
                    setBotSettings({ ...botSettings, offline_alerts_enabled: newVal });
                    toast.success(newVal ? "Offline alerts ON" : "Offline alerts OFF");
                  } catch (e) { toast.error("Failed to update"); }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors ${botSettings.offline_alerts_enabled ? 'bg-[#25D366]' : 'bg-[#27272A]'}`}
                data-testid="toggle-offline-alerts"
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${botSettings.offline_alerts_enabled ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
            <div className="text-xs text-gray-500">
              {botSettings.last_offline_check ? `Last check: ${new Date(botSettings.last_offline_check).toLocaleTimeString()}` : "Never checked"}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 border-[#27272A] text-xs"
              onClick={async () => {
                toast.info("Checking machines...");
                try {
                  const res = await axios.post(`${API}/bot/run-offline-check`);
                  if (res.data.success) {
                    if (res.data.newly_offline > 0) toast.warning(`${res.data.newly_offline} machine(s) newly offline! Alert sent.`);
                    else if (res.data.back_online > 0) toast.success(`${res.data.back_online} machine(s) back online!`);
                    else toast.success(`All good! ${res.data.current_offline} offline total.`);
                  } else toast.error(res.data.message || res.data.error);
                } catch (e) { toast.error("Failed"); }
              }}
              data-testid="manual-check-offline"
            >
              Check Now
            </Button>
          </div>
        </div>
        
        {/* Admin Phone */}
        <div className="mt-4 flex items-center gap-3">
          <Label className="text-xs text-gray-400 whitespace-nowrap">Admin Phone:</Label>
          <Input
            value={botSettings.admin_phone || ""}
            onChange={(e) => setBotSettings({ ...botSettings, admin_phone: e.target.value })}
            className="bg-[#0A0A0A] border-[#27272A] text-sm max-w-[200px]"
            placeholder="+905464678877"
          />
          <Button
            size="sm"
            variant="outline"
            className="border-[#27272A] text-xs"
            onClick={async () => {
              try {
                await axios.put(`${API}/bot-settings`, { admin_phone: botSettings.admin_phone });
                toast.success("Admin phone updated");
              } catch (e) { toast.error("Failed"); }
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[#27272A] text-xs text-[#25D366]"
            onClick={async () => {
              toast.info("Sending test message...");
              try {
                const res = await axios.post(`${API}/whatsapp/test-send?to=${encodeURIComponent(botSettings.admin_phone)}`);
                if (res.data.success) toast.success("Test message sent! Check WhatsApp.");
                else toast.error(res.data.error);
              } catch (e) { toast.error("Failed to send test"); }
            }}
            data-testid="test-whatsapp-btn"
          >
            Test WhatsApp
          </Button>
        </div>

        {/* Fleet Monitor Phone — offline/recovery alerts only, nothing else */}
        <div className="mt-3 flex items-center gap-3">
          <Label className="text-xs text-gray-400 whitespace-nowrap">Fleet Monitor:</Label>
          <Input
            value={botSettings.fleet_monitor_phone || ""}
            onChange={(e) => setBotSettings({ ...botSettings, fleet_monitor_phone: e.target.value })}
            className="bg-[#0A0A0A] border-[#27272A] text-sm max-w-[200px]"
            placeholder="+961XXXXXXXX"
          />
          <Button
            size="sm"
            variant="outline"
            className="border-[#27272A] text-xs"
            onClick={async () => {
              try {
                await axios.put(`${API}/bot-settings`, { fleet_monitor_phone: botSettings.fleet_monitor_phone || "" });
                toast.success(botSettings.fleet_monitor_phone ? "Fleet monitor number updated" : "Fleet monitor number cleared");
              } catch (e) { toast.error("Failed"); }
            }}
          >
            Save
          </Button>
          <span className="text-xs text-gray-500">
            Gets OFFLINE / RECOVERED alerts for every machine — nothing else. Leave empty to disable.
          </span>
        </div>
      </div>

      {/* Payment Claims (customer self-confirmation queue) */}
      <div className="card p-4 sm:p-6" data-testid="payment-claims-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DollarSign className="text-[#FFD700]" size={20} />
            <h2 className="text-lg font-bold text-white">Payment Claims</h2>
            {paymentClaims.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-xs font-bold">
                {paymentClaims.length} pending
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" className="border-[#27272A] text-xs" onClick={fetchPaymentClaims}>
            Refresh
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          When a customer texts <code className="bg-[#0A0A0A] px-1 rounded">paid</code> to the WhatsApp bot, their claim appears here for your approval.
        </p>
        {paymentClaims.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No pending claims.</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {paymentClaims.map((claim) => (
              <div key={claim.id} className="bg-[#0A0A0A] rounded-lg border border-[#27272A] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-bold text-white">{claim.customer_name}</span>
                    <span className="text-xs text-gray-500 ml-2">{claim.phone}</span>
                  </div>
                  <span className="text-xs text-gray-500">{new Date(claim.created_at).toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                  <div><span className="text-gray-500">Month:</span> <span className="text-white">{claim.month || '—'}</span></div>
                  <div><span className="text-gray-500">Expected:</span> <span className="text-[#FFD700] font-bold">${claim.amount_expected || '—'}</span></div>
                </div>
                <p className="text-xs text-gray-400 italic mb-3 bg-[#1A1A1A] p-2 rounded">"{claim.raw_message}"</p>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-[#00E054] hover:bg-[#00E054]/80 text-black flex-1" onClick={() => approveClaim(claim.id)} data-testid={`approve-claim-${claim.id}`}>
                    ✅ Approve
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 flex-1" onClick={() => rejectClaim(claim.id)} data-testid={`reject-claim-${claim.id}`}>
                    ❌ Reject
                  </Button>
                  <Button size="sm" variant="outline" title="Dismiss — nothing is sent to the customer" className="border-[#27272A] text-gray-400 hover:bg-white/5 px-3" onClick={() => dismissClaim(claim.id)} data-testid={`dismiss-claim-${claim.id}`}>
                    ✕
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* WhatsApp Bot QR Code (customer onboarding) */}
      <div className="card p-4 sm:p-6" data-testid="whatsapp-qr-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="text-[#25D366]" size={20} />
            <h2 className="text-lg font-bold text-white">Bot QR Code</h2>
          </div>
          <Button variant="outline" size="sm" className="border-[#27272A] text-xs" onClick={showQrCode}>
            {qrInfo ? "Refresh" : "Generate"}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Print this QR or share the link. Customers scan it → opens WhatsApp chat with the bot pre-filled with "Hi" → bot replies with welcome menu.
        </p>
        {qrInfo ? (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <img src={qrInfo.qr_image_url} alt="WhatsApp Bot QR" className="w-40 h-40 bg-white p-2 rounded-lg" />
            <div className="flex-1 text-sm">
              <div className="mb-2"><span className="text-gray-500">Bot phone:</span> <span className="font-mono text-white">{qrInfo.bot_phone}</span></div>
              <div className="mb-3 break-all"><span className="text-gray-500">Direct link:</span> <a href={qrInfo.wa_link} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">{qrInfo.wa_link}</a></div>
              <Button size="sm" variant="outline" className="border-[#27272A] text-xs" onClick={() => navigator.clipboard.writeText(qrInfo.wa_link)}>
                Copy link
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">Click "Generate" to view the QR code.</p>
        )}
      </div>

      {/* Send Per-Customer (PDF invoice + Stats screenshot) */}
      <div className="card p-4 sm:p-6" data-testid="whatsapp-per-customer">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="text-[#FFD700]" size={20} />
          <h2 className="text-lg font-bold text-white">Send to Customer</h2>
          <span className="text-xs text-gray-500 ml-2">PDF invoice or live stats screenshot</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CustomerCombobox
            customers={customers}
            filter={(c) => !!c.phone}
            value={selectedCustomerForSend || ""}
            onChange={(id) => setSelectedCustomerForSend(id)}
            placeholder="— pick a customer —"
            accent="#FFD700"
            testid="select-customer-for-send"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="bg-[#06B6D4] hover:bg-[#06B6D4]/80 text-black flex-1"
              disabled={!selectedCustomerForSend || sendingStats}
              onClick={async () => {
                setSendingStats(true);
                try {
                  const res = await axios.post(`${API}/whatsapp/send-stats-image`, { customer_id: selectedCustomerForSend });
                  if (res.data.success) toast.success(`Stats image sent to ${res.data.sent_to}`);
                  else toast.error(`Failed: ${res.data.error}`);
                } catch (e) { toast.error("Failed to send"); }
                setSendingStats(false);
              }}
              data-testid="send-stats-image"
            >
              {sendingStats ? "Sending..." : "📊 Send Stats"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-[#27272A] text-xs flex-1"
              disabled={!selectedCustomerForSend}
              onClick={async () => {
                if (!selectedCustomerForSend) return;
                const w = window.open("", "_blank");
                if (!w) { toast.error("Allow pop-ups to preview the stats image"); return; }
                try {
                  const r = await axios.get(`${API}/customers/${selectedCustomerForSend}/stats-preview.png`, { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  w.location.href = url;
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (e) {
                  w.close();
                  toast.error("Couldn't load the stats preview");
                }
              }}
            >
              👁 Preview
            </Button>
          </div>
        </div>
        <div className="mt-3 bg-[#0A0A0A] rounded-lg p-3 border border-[#27272A]">
          <p className="text-xs text-gray-500 mb-2">📄 Send PDF invoice for an unpaid bill:</p>
          <div className="flex gap-2">
            <select
              className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-xs text-white outline-none flex-1"
              value={selectedPaymentForPdf || ""}
              onChange={(e) => setSelectedPaymentForPdf(e.target.value)}
              data-testid="select-payment-for-pdf"
              disabled={!selectedCustomerForSend}
            >
              <option value="">— pick an unpaid invoice —</option>
              {unpaidPaymentsForSelected.map(p => (
                <option key={p.id} value={p.id}>{p.month} — ${p.amount}</option>
              ))}
            </select>
            <Button
              size="sm"
              className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black"
              disabled={!selectedPaymentForPdf || sendingPdf}
              onClick={async () => {
                setSendingPdf(true);
                try {
                  const res = await axios.post(`${API}/whatsapp/send-invoice-pdf`, { payment_id: selectedPaymentForPdf });
                  if (res.data.success) toast.success("Invoice PDF sent!");
                  else toast.error(`Failed: ${res.data.error}`);
                } catch (e) { toast.error("Failed to send"); }
                setSendingPdf(false);
              }}
              data-testid="send-invoice-pdf"
            >
              {sendingPdf ? "..." : "Send PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Broadcast Message to All Customers */}
      <div className="card p-4 sm:p-6" data-testid="whatsapp-broadcast">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="text-[#25D366]" size={20} />
          <h2 className="text-lg font-bold text-white">Broadcast Message</h2>
          <span className="text-xs text-gray-500 ml-2">Send to all active customers</span>
        </div>
        <textarea
          className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 text-sm text-white resize-none focus:border-[#25D366] outline-none"
          rows={4}
          placeholder="Type your message here... It will be sent to all active customers via WhatsApp."
          value={broadcastMessage}
          onChange={(e) => setBroadcastMessage(e.target.value)}
          data-testid="broadcast-input"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-gray-500">{broadcastMessage.length} characters</span>
          <Button
            size="sm"
            disabled={!broadcastMessage.trim() || broadcastSending}
            className="bg-[#25D366] hover:bg-[#25D366]/80 text-black font-medium"
            onClick={async () => {
              if (!window.confirm(`Send this message to ALL active customers?`)) return;
              setBroadcastSending(true);
              try {
                const res = await axios.post(`${API}/whatsapp/broadcast`, { message: broadcastMessage });
                if (res.data.success) {
                  toast.success(`Sent to ${res.data.sent} customers! (${res.data.failed} failed)`);
                  setBroadcastMessage("");
                } else {
                  toast.error(res.data.error);
                }
              } catch (e) {
                toast.error("Failed to send broadcast");
              }
              setBroadcastSending(false);
            }}
            data-testid="broadcast-send-btn"
          >
            {broadcastSending ? "Sending..." : "Send to All"}
          </Button>
        </div>
      </div>

      {/* WhatsApp Message History */}
      <div className="card p-4 sm:p-6" data-testid="whatsapp-history">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="text-[#25D366]" size={20} />
            <h2 className="text-lg font-bold text-white">Message History</h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-[#27272A] text-xs"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/whatsapp/history/all`);
                setMessageHistory(res.data);
              } catch (e) { toast.error("Failed to load history"); }
            }}
            data-testid="load-history-btn"
          >
            Load History
          </Button>
        </div>
        {messageHistory && messageHistory.length > 0 ? (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {messageHistory.map((cust, idx) => (
              <div key={idx} className="bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                <button
                  onClick={() => setExpandedHistory(expandedHistory === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-3 text-left"
                >
                  <div>
                    <span className="text-sm font-medium text-white">{cust.customer_name}</span>
                    <span className="text-xs text-gray-500 ml-2">{cust.phone}</span>
                  </div>
                  <span className="text-xs text-gray-500">{cust.messages.length} messages</span>
                </button>
                {expandedHistory === idx && (
                  <div className="px-3 pb-3 space-y-2 border-t border-[#27272A] pt-2">
                    {cust.messages.slice(0, 10).map((msg, mi) => (
                      <div key={mi} className={`text-xs p-2 rounded ${msg.status === 'sent' ? 'bg-[#1A1A1A]' : 'bg-red-900/20'}`}>
                        <div className="flex justify-between mb-1">
                          <span className={`font-medium ${msg.status === 'sent' ? 'text-[#25D366]' : 'text-red-400'}`}>
                            {msg.type} • {msg.status}
                          </span>
                          <span className="text-gray-500">{new Date(msg.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-gray-400 whitespace-pre-wrap line-clamp-3">{msg.message.substring(0, 150)}...</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No messages sent yet. Click "Load History" to view sent messages.</p>
        )}
      </div>

        </TabsContent>

        <TabsContent value="repairs" className="space-y-6 mt-6">
      {/* Repairs */}
      <div className="card p-4 sm:p-6" data-testid="repairs-section">
        <div className="page-header mb-4">
          <div className="flex items-center gap-2">
            <Wrench className="text-[#F59E0B]" size={20} />
            <h2 className="text-lg font-bold text-white">Repairs</h2>
            {repairs.filter(r => r.status === 'unpaid').length > 0 && (
              <span className="text-xs bg-[#F59E0B]/20 text-[#F59E0B] px-2 py-0.5 rounded-full">
                {repairs.filter(r => r.status === 'unpaid').length} unpaid
              </span>
            )}
          </div>
          <Button onClick={() => { setRepairForm({ customer_id: "", description: "", cost: "", cost_tbd: false }); setShowRepairModal(true); }} className="bg-[#F59E0B] text-black w-full sm:w-auto" data-testid="add-repair-btn">
            <Plus size={16} className="mr-2" /> Add Repair
          </Button>
        </div>

        {repairs.length > 0 ? (
          <div className="space-y-2 max-h-[420px] sm:max-h-[300px] overflow-y-auto">
            {repairs.map(repair => (
              <div key={repair.id} className={`bg-[#0A0A0A] rounded-lg border p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${repair.status === 'unpaid' ? 'border-[#F59E0B]/30' : 'border-[#27272A]'}`}>
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-2 sm:justify-start">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-white truncate">{repair.customer_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${repair.status === 'unpaid' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#00E054]/20 text-[#00E054]'}`}>
                        {repair.status}
                      </span>
                    </div>
                    <button className="text-lg font-bold text-white sm:hidden shrink-0 hover:text-[#F59E0B] underline decoration-dotted underline-offset-4" title="Click to set the repair price" onClick={async () => {
                        const cur = repair.cost_tbd ? "" : (repair.cost ?? "");
                        const val = window.prompt(`Set repair price for ${repair.customer_name} ($). Leave empty for TBD:`, cur);
                        if (val === null) return;
                        const trimmed = String(val).trim();
                        if (trimmed === "") {
                          try { await axios.put(`${API}/repairs/${repair.id}`, { cost_tbd: true }); toast.success("Marked TBD"); fetchData(); }
                          catch { toast.error("Failed"); }
                          return;
                        }
                        const num = parseFloat(trimmed);
                        if (isNaN(num) || num < 0) { toast.error("Enter a valid number"); return; }
                        try { await axios.put(`${API}/repairs/${repair.id}`, { cost: num, cost_tbd: false }); toast.success("Price set"); fetchData(); }
                        catch { toast.error("Failed"); }
                      }}>{repair.cost_tbd ? "TBD" : (repair.cost || repair.cost === 0 ? `$${Number(repair.cost).toLocaleString()}` : "Set price")}</button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 break-words">{repair.description}</p>
                  <p className="text-xs text-gray-500">{new Date(repair.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button className="text-lg font-bold text-white hidden sm:inline mr-1 hover:text-[#F59E0B] underline decoration-dotted underline-offset-4" title="Click to set the repair price" onClick={async () => {
                        const cur = repair.cost_tbd ? "" : (repair.cost ?? "");
                        const val = window.prompt(`Set repair price for ${repair.customer_name} ($). Leave empty for TBD:`, cur);
                        if (val === null) return;
                        const trimmed = String(val).trim();
                        if (trimmed === "") {
                          try { await axios.put(`${API}/repairs/${repair.id}`, { cost_tbd: true }); toast.success("Marked TBD"); fetchData(); }
                          catch { toast.error("Failed"); }
                          return;
                        }
                        const num = parseFloat(trimmed);
                        if (isNaN(num) || num < 0) { toast.error("Enter a valid number"); return; }
                        try { await axios.put(`${API}/repairs/${repair.id}`, { cost: num, cost_tbd: false }); toast.success("Price set"); fetchData(); }
                        catch { toast.error("Failed"); }
                      }}>{repair.cost_tbd ? "TBD" : (repair.cost || repair.cost === 0 ? `$${Number(repair.cost).toLocaleString()}` : "Set price")}</button>
                  <Button size="sm" variant="outline" className="border-gray-500 text-gray-300 text-xs"
                    onClick={() => {
                      window.open(`${API}/repairs/${repair.id}/invoice.pdf?admin_token=${encodeURIComponent(getAdminToken())}`, "_blank");
                    }}
                    title="Download repair invoice PDF"
                  >PDF</Button>
                  <Button size="sm" variant="outline" className="border-[#25D366] text-[#25D366] text-xs"
                    onClick={async () => {
                      if (!window.confirm(`Send a repair invoice PDF to ${repair.customer_name} on WhatsApp?`)) return;
                      try {
                        const r = await axios.post(`${API}/whatsapp/send-repair-invoice-pdf`, { repair_id: repair.id });
                        if (r.data?.success) toast.success(`PDF invoice sent to ${repair.customer_name}`);
                        else toast.error(r.data?.error || "Send failed");
                      } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
                    }}
                    title="Send repair invoice PDF via WhatsApp"
                  >Send PDF</Button>
                  {repair.status === 'unpaid' && (
                    <Button size="sm" variant="outline" className="border-[#00E054] text-[#00E054] text-xs"
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/repairs/${repair.id}`, { status: "paid" });
                          toast.success(`Repair marked as paid for ${repair.customer_name}`);
                          fetchData();
                        } catch (e) { toast.error("Failed"); }
                      }}
                    >Mark Paid</Button>
                  )}
                  <Button size="icon" variant="ghost" className="text-red-400 ml-auto sm:ml-0"
                    onClick={async () => {
                      if (!window.confirm("Delete this repair?")) return;
                      try {
                        await axios.delete(`${API}/repairs/${repair.id}`);
                        toast.success("Repair deleted");
                        fetchData();
                      } catch (e) { toast.error("Failed"); }
                    }}
                  ><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No repairs recorded.</p>
        )}
      </div>

      {/* Repair Modal */}
      {showRepairModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#1A1A1A] rounded-xl border border-[#27272A] p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-4">Add Repair</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-400">Customer</Label>
                <CustomerCombobox
                  customers={customers}
                  filter={(c) => c.status !== 'paused'}
                  value={repairForm.customer_id}
                  onChange={(id) => setRepairForm({ ...repairForm, customer_id: id })}
                  placeholder="Select customer..."
                  showPhone={false}
                  accent="#EF4444"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Description (what was repaired)</Label>
                <Input
                  className="bg-[#0A0A0A] border-[#27272A]"
                  placeholder="e.g., Fan replacement, PSU repair..."
                  value={repairForm.description}
                  onChange={(e) => setRepairForm({ ...repairForm, description: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Cost ($)</Label>
                <Input
                  type="number"
                  className="bg-[#0A0A0A] border-[#27272A] disabled:opacity-40"
                  placeholder={repairForm.cost_tbd ? "Unknown — to be determined" : "0"}
                  value={repairForm.cost_tbd ? "" : repairForm.cost}
                  disabled={repairForm.cost_tbd}
                  onChange={(e) => setRepairForm({ ...repairForm, cost: e.target.value })}
                />
                <label className="flex items-center gap-2 mt-2 text-xs text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={repairForm.cost_tbd}
                         onChange={(e) => setRepairForm({ ...repairForm, cost_tbd: e.target.checked })}
                         data-testid="repair-cost-tbd" />
                  Cost unknown for now (boards at the repair center — set the price later)
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1 border-[#27272A]" onClick={() => setShowRepairModal(false)}>Cancel</Button>
              <Button className="flex-1 bg-[#F59E0B] text-black" onClick={async () => {
                if (!repairForm.customer_id || !repairForm.description || (!repairForm.cost && !repairForm.cost_tbd)) {
                  toast.error("Fill all fields (or tick 'Cost unknown')"); return;
                }
                try {
                  await axios.post(`${API}/repairs`, repairForm);
                  toast.success("Repair added");
                  setShowRepairModal(false);
                  fetchData();
                } catch (e) { toast.error("Failed"); }
              }}>Add Repair</Button>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Logs */}
      <div className="card p-4 sm:p-6">
        <div className="page-header mb-4">
          <div className="flex items-center gap-2">
            <Wrench className="text-[#EAB308]" size={20} />
            <h2 className="text-lg font-bold text-white">Maintenance Logs</h2>
          </div>
          <Button onClick={() => setShowLogModal(true)} className="bg-[#EAB308] text-black w-full sm:w-auto" data-testid="add-log-btn">
            <Plus size={16} className="mr-2" />
            Add Log
          </Button>
        </div>

        <div className="space-y-2">
          {maintenanceLogs.slice(0, 10).map(log => (
            <div key={log.id} className="flex items-center justify-between gap-3 bg-[#0A0A0A] p-3 rounded-lg border border-[#27272A]">
              <div className="min-w-0">
                <p className="text-white break-words">{log.description}</p>
                <p className="text-sm text-gray-500">
                  {getCustomerName(log.customer_id)} • {log.machine_info || "All machines"} • {new Date(log.date).toLocaleDateString()}
                </p>
              </div>
              <Button size="icon" variant="ghost" className="text-red-400 shrink-0" onClick={() => handleDeleteLog(log)}>
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
          {maintenanceLogs.length === 0 && (
            <p className="text-center text-gray-500 py-4">No maintenance logs</p>
          )}
        </div>
      </div>

        </TabsContent>
      </Tabs>

      {/* Account Modal */}
      <Dialog open={showAccountModal} onOpenChange={setShowAccountModal}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle>{editingAccount ? "Edit Account" : "Create Account"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer</Label>
              <div className="mt-1">
                <CustomerCombobox
                  customers={customers}
                  value={accountForm.customer_id}
                  onChange={(v) => {
                    const customer = customers.find(c => c.id === v);
                    const phone = customer?.phone?.replace(/\D/g, "") || "";
                    const workerName = customer?.name?.toLowerCase().replace(/\s/g, "") || "";
                    setAccountForm({
                      ...accountForm,
                      customer_id: v,
                      username: workerName,
                      password: phone.slice(-4) || "0000",
                      worker_name: workerName
                    });
                  }}
                  showPhone={false}
                  placeholder="Select customer"
                />
              </div>
            </div>
            <div>
              <Label>Username / Worker Name (ViaBTC account name)</Label>
              <Input 
                value={accountForm.worker_name} 
                onChange={(e) => {
                  const value = e.target.value.toLowerCase().replace(/\s/g, "");
                  setAccountForm({ ...accountForm, username: value, worker_name: value });
                }} 
                className="bg-[#0A0A0A] border-[#27272A] mt-1 font-mono" 
                placeholder="e.g., hamidwk"
              />
              <p className="text-xs text-gray-500 mt-1">This is both the login username AND the ViaBTC sub-account name</p>
            </div>
            <div>
              <Label>Password (last 4 digits of phone)</Label>
              <Input value={accountForm.password} onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] mt-1" />
            </div>
            <div>
              <Label>ViaBTC API Key (auto-synced)</Label>
              <Input 
                value={accountForm.viabtc_api_key} 
                onChange={(e) => setAccountForm({ ...accountForm, viabtc_api_key: e.target.value })} 
                className="bg-[#0A0A0A] border-[#27272A] mt-1 font-mono text-xs" 
                placeholder="Click 'Sync ViaBTC API Keys' to auto-fill" 
                readOnly
              />
              <p className="text-xs text-gray-500 mt-1">Auto-synced from ViaBTC. Click "Sync ViaBTC API Keys" button.</p>
            </div>
            <div>
              <Label className="text-[#00E054]">Watcher Link URL (Recommended - No IP whitelist needed)</Label>
              <Input 
                value={accountForm.watcher_url || ""} 
                onChange={(e) => setAccountForm({ ...accountForm, watcher_url: e.target.value })} 
                className="bg-[#0A0A0A] border-[#00E054]/30 mt-1 text-xs" 
                placeholder="https://www.viabtc.com/en/observer/worker?access_key=xxx&coin=LTC" 
              />
              <p className="text-xs text-gray-500 mt-1">Get from ViaBTC → Observer → Copy watcher link</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountModal(false)} className="border-[#27272A]">Cancel</Button>
            <Button onClick={handleSaveAccount} className="bg-[#00E054] text-black">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Log Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle>Add Maintenance Log</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer</Label>
              <div className="mt-1">
                <CustomerCombobox
                  customers={customers}
                  value={logForm.customer_id}
                  onChange={(v) => setLogForm({ ...logForm, customer_id: v })}
                  showPhone={false}
                  placeholder="Select customer"
                  accent="#EAB308"
                />
              </div>
            </div>
            <div>
              <Label>Machine Info (optional)</Label>
              <Input value={logForm.machine_info} onChange={(e) => setLogForm({ ...logForm, machine_info: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] mt-1" placeholder="e.g., L9 #1" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={logForm.description} onChange={(e) => setLogForm({ ...logForm, description: e.target.value })} className="bg-[#0A0A0A] border-[#27272A] mt-1" placeholder="e.g., Cleaned fans" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogModal(false)} className="border-[#27272A]">Cancel</Button>
            <Button onClick={handleSaveLog} className="bg-[#EAB308] text-black">Add Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showHelperModal} onOpenChange={setShowHelperModal}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle>Add Helper Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Username</Label>
              <Input
                value={helperForm.username}
                onChange={(e) => setHelperForm({ ...helperForm, username: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] mt-1"
                placeholder="e.g., mike"
                data-testid="helper-form-username"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                value={helperForm.password}
                onChange={(e) => setHelperForm({ ...helperForm, password: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] mt-1"
                placeholder="Choose a password"
                data-testid="helper-form-password"
              />
            </div>
            <div>
              <Label>Display Name (optional)</Label>
              <Input
                value={helperForm.display_name}
                onChange={(e) => setHelperForm({ ...helperForm, display_name: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] mt-1"
                placeholder="e.g., Mike (Technician)"
              />
            </div>
            <p className="text-xs text-gray-500">
              They'll log in at <span className="text-[#00C2FF]">wkbeast.org/helper</span> — view-only, no access to payments, customer data, or settings.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHelperModal(false)} className="border-[#27272A]">Cancel</Button>
            <Button onClick={handleAddHelper} disabled={savingHelper} className="bg-[#00C2FF] text-black">
              {savingHelper ? "Saving..." : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
